<script setup lang="ts">
import type { LinkRecordsSubmit } from './LinkRecordsDialog.vue';
import type { SuggestionBlocker, SuggestionCluster, SuggestionListMode } from '@/components/domain/record-link/fields';
import { ChevronDown, ChevronUp } from 'lucide';
import { computed, ref, watch } from 'vue';
import { SphButton } from '@/components/base/button';
import { useI18n } from '@/composables/i18n';
import { useCardText } from './cardText';
import LinkRecordsDialog from './LinkRecordsDialog.vue';
import RecordDifferences from './RecordDifferences.vue';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    cluster: SuggestionCluster
    canManage: boolean
    mode: SuggestionListMode
    pending: boolean
    expandAll: boolean
}>();

const emit = defineEmits<{
    confirm: [suggestionIds: number[]]
    dismiss: [suggestionIds: number[]]
    reopen: [suggestionIds: number[]]
    'link-records': [payload: LinkRecordsSubmit]
}>();

const { i18n } = useI18n();
const { distinctNames, recordsLine: recordsLineOf, differencesSummary: differencesSummaryOf } = useCardText();

const open = ref(props.expandAll);
watch(() => props.expandAll, (expandAll) => {
    open.value = expandAll;
});

const linkDialogOpen = ref(false);

const suggestionIds = computed(() => props.cluster.suggestions.map((suggestion) => suggestion.id));
const needsRecordChoice = computed(() => props.cluster.members.length > 2);
const hasSameClubDuplicate = computed(() => props.cluster.duplicateInstitutionNames.length > 0);

const name = computed(() => distinctNames(props.cluster.members.map((member) => member.displayName)));
const recordsLine = computed(() => recordsLineOf(props.cluster.members.map((member) => member.institutionName)));
const differencesSummary = computed(() => differencesSummaryOf(props.cluster.divergentFields));

// the same-club double row has its badge; the other blockers need a sentence
const blockerNotes: Partial<Record<SuggestionBlocker, string>> = {
    dismissed_pair: 'Ein Vorschlag zu dieser Person wurde bereits verworfen.',
    multiple_groups: 'Die Datensätze gehören bereits zu verschiedenen Verknüpfungen.',
};
const blockerLines = computed(() => (props.mode === 'dismissed'
    ? []
    : props.cluster.blockers.flatMap((blocker) => (blockerNotes[blocker] === undefined ? [] : [i18n.t(blockerNotes[blocker])]))));

const dismissalNotes = computed(() => {
    if (props.mode !== 'dismissed') {
        return [];
    }
    const notes = props.cluster.suggestions.map((suggestion) => (suggestion.decidedByName === null
        ? i18n.t('Am {date} durch Aufheben einer Verknüpfung verworfen.', { date: suggestion.decidedAt ?? '—' })
        : i18n.t('Am {date} von {name} verworfen.', { date: suggestion.decidedAt ?? '—', name: suggestion.decidedByName })));

    return notes.filter((note, index) => notes.indexOf(note) === index);
});

function link() {
    if (needsRecordChoice.value) {
        linkDialogOpen.value = true;
        return;
    }
    emit('confirm', suggestionIds.value);
}

function submitLink(payload: LinkRecordsSubmit) {
    // the whole cluster goes through the pair confirms, which also cope with records already in a group
    if (payload.memberIds.length === props.cluster.members.length) {
        emit('confirm', suggestionIds.value);
        payload.done(true);
        return;
    }
    emit('link-records', payload);
}
</script>

<template>
    <article class="suggestion-card">
        <div class="suggestion-card__head">
            <div class="suggestion-card__heading">
                <h3 class="kgr-headline kgr-headline--h4 suggestion-card__name">
                    {{ name }}
                </h3>
                <div class="kgr-text kgr-text--light suggestion-card__records">
                    {{ recordsLine }}
                </div>
            </div>
            <span
                v-if="hasSameClubDuplicate"
                class="kgr-badge kgr-badge--warning suggestion-card__badge"
            >{{ $t('Dublette') }}</span>
            <span
                v-else-if="cluster.exact"
                class="kgr-badge kgr-badge--success suggestion-card__badge"
            >{{ $t('Exakter Treffer') }}</span>
        </div>

        <div
            v-if="differencesSummary !== null"
            class="suggestion-card__summary"
        >
            <SphButton
                :label="differencesSummary"
                variant="transparent"
                size="sm"
                :icon-right="open ? ChevronUp : ChevronDown"
                :aria-expanded="open"
                @click="open = !open"
            />
        </div>

        <RecordDifferences
            v-if="open && differencesSummary !== null"
            :records="cluster.members"
            :fields="cluster.fields"
            :divergent-fields="cluster.divergentFields"
            class="suggestion-card__differences"
        />

        <div
            v-if="dismissalNotes.length > 0 || blockerLines.length > 0"
            class="kgr-text kgr-text--light suggestion-card__notes"
        >
            <div
                v-for="note in [...blockerLines, ...dismissalNotes]"
                :key="note"
            >
                {{ note }}
            </div>
        </div>

        <div
            v-if="canManage"
            class="suggestion-card__actions"
        >
            <SphButton
                v-if="mode === 'dismissed'"
                :label="$t('Wieder öffnen')"
                variant="primary"
                size="sm"
                class="flex-1"
                :disabled="pending"
                @click="emit('reopen', suggestionIds)"
            />
            <template v-else>
                <SphButton
                    :label="$t('Verwerfen')"
                    size="sm"
                    class="flex-1"
                    :disabled="pending"
                    @click="emit('dismiss', suggestionIds)"
                />
                <SphButton
                    v-if="mode !== 'same-club'"
                    :label="needsRecordChoice ? $t('Verknüpfen …') : $t('Verknüpfen')"
                    variant="primary"
                    size="sm"
                    class="flex-1"
                    :disabled="pending"
                    @click="link"
                />
            </template>
        </div>

        <LinkRecordsDialog
            v-if="needsRecordChoice"
            v-model:state="linkDialogOpen"
            :cluster="cluster"
            :name="name"
            @submit="submitLink"
        />
    </article>
</template>

<style scoped lang="scss">
.suggestion-card {
    display: flex;
    flex-direction: column;
    min-width: 0;
    border: 1px solid var(--kgr-card-border-color);
    border-radius: 12px;
    background-color: var(--kgr-color-white);
    box-shadow: 0 1px 2px color-mix(in srgb, var(--kgr-color-black) 6%, transparent);
}

.suggestion-card__head {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 1rem 1rem 0.5rem;
}

.suggestion-card__heading {
    flex: 1;
    min-width: 0;
}

.suggestion-card__name {
    overflow-wrap: anywhere;
}

.suggestion-card__records {
    margin-top: 0.25rem;
    overflow-wrap: anywhere;
}

.suggestion-card__badge {
    flex-shrink: 0;
}

.suggestion-card__summary {
    padding: 0 0.5rem;

    :deep(.sph-button) {
        height: auto;
        max-width: 100%;
        white-space: normal;
        text-align: left;
        line-height: 1.3;
        font-weight: var(--font-weight-semibold);
        color: var(--kgr-color-warning);
    }

    :deep([data-label]) {
        min-width: 0;
        overflow-wrap: anywhere;
    }
}

.suggestion-card__differences {
    padding: 0.25rem 1rem 0.5rem;
}

.suggestion-card__notes {
    padding: 0.25rem 1rem 0;
    overflow-wrap: anywhere;
}

.suggestion-card__actions {
    display: flex;
    gap: 0.5rem;
    margin-top: auto;
    padding: 0.75rem 1rem 1rem;
}
</style>
